import React, { useEffect, useRef, useState } from 'react';
import { Badge, Popover } from '@mui/material'; 
import io from 'socket.io-client';
import NotificationList from './notificationList'; // Adjust the path if needed
import { Notifications as NotificationsIcon } from '@mui/icons-material';
const DUPLICATE_WINDOW_MS = 2 * 60 * 1000;
const notificationServerUri = String(process.env.REACT_APP_NOTIFICATION_SERVER_URI || '').trim();

export default function NotificationBadge({ currentStaffImage, email }) {
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const recentNotificationRef = useRef(new Map());
  const socketRef = useRef(null);

  useEffect(() => {
    if (!email || !notificationServerUri) return undefined;

    const socket = io(notificationServerUri);
    socketRef.current = socket;

    // Join the room using email for targeted notifications
    socket.emit('join', email);

    // Listen for notifications from the server
    const handleNotification = (data) => {
      const message = String(data?.message || '').trim();
      if (!message) return;

      const now = Date.now();
      const lastSeenAt = recentNotificationRef.current.get(message) || 0;
      if (now - lastSeenAt < DUPLICATE_WINDOW_MS) {
        return;
      }

      recentNotificationRef.current.set(message, now);
      for (const [key, seenAt] of recentNotificationRef.current.entries()) {
        if (now - seenAt >= DUPLICATE_WINDOW_MS) {
          recentNotificationRef.current.delete(key);
        }
      }

      setNotifications((prev) => [
        ...prev,
        { id: `${now}-${message}`, message },
      ]);
    };

    socket.on('notification', handleNotification);

    // Cleanup listener on unmount
    return () => {
      socket.off('notification', handleNotification);
      socket.emit('leave', email);
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [email]);

  // Open the popover when the badge is clicked
  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Remove a single notification
  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
  };

  const open = Boolean(anchorEl);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Badge
        badgeContent={notifications.length}
        color="error"
        onClick={handleIconClick}
        style={{ cursor: 'pointer' }}
      >
        <NotificationsIcon sx={{ fontSize: 24 }} />
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: { zIndex: 1300, padding: 8, minWidth: '250px' },
        }}
      >
        <NotificationList 
          notifications={notifications} 
          removeNotification={removeNotification}
          clearAll={clearAll}
        />
      </Popover>
    </div>
  );
}
